import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Car, Phone, Calendar, MapPin, CheckCircle, AlertCircle, User, Hash, Users, FileText, Clock } from 'lucide-react'
import { toast } from 'react-hot-toast'
import { useAuth } from '../../context/AuthContext'
import api from '../../config/api'
import axios from 'axios'
import { useNavigate } from 'react-router-dom'

export default function VehicleRegistration() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [formData, setFormData] = useState({
    ownerName: user?.name || '',
    vehicleType: '',
    licensePlate: '',
    pincode: '',
    state: '',
    city: '',
    capacity: '',
    contactNumber: '',
    availabilityDate: '',
    availableDays: '',
    isCurrentlyAvailable: false,
    additionalNotes: '',
    vehicleImage: ''
  })
  const [imagePreview, setImagePreview] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [successMessage, setSuccessMessage] = useState('')
  const [registeredVehicle, setRegisteredVehicle] = useState(null)
  
  // // Check if user is logged in
  // useEffect(() => {
  //   if (!user) {
  //     toast.error('Please log in to register a vehicle')
  //     navigate('/login')
  //   }
  // }, [user, navigate])

  const vehicleTypes = [
    { value: 'car', label: 'Car' },
    { value: 'bike', label: 'Bike' },
    { value: 'ambulance', label: 'Ambulance' },
    { value: 'van', label: 'Van' },
    { value: 'other', label: 'Other' }
  ]

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    console.log('Form submission started');

    // Validate all required fields
    const requiredFields = [
      'ownerName',
      'contactNumber', 
      'vehicleType',
      'licensePlate',
      'pincode',
      'state',
      'city',
      'capacity',
      'availabilityDate',
      'availableDays'
    ];

    const missingFields = requiredFields.filter(field => !formData[field]);

    if (missingFields.length > 0) {
      const missingFieldNames = missingFields.join(', ');
      setErrorMessage(`Please complete these required fields: ${missingFieldNames}`);
      toast.error('Please complete all required fields before submitting');
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare data for backend - ensure proper data types
      const vehicleData = {
        ...formData,
        capacity: parseInt(formData.capacity),
        availableDays: parseInt(formData.availableDays),
        isCurrentlyAvailable: formData.isCurrentlyAvailable || false,
        additionalNotes: formData.additionalNotes || ''
      };

      console.log('Sending vehicle data to backend:', vehicleData);

      // Create a custom instance of axios for this request to avoid interceptors
      const axiosInstance = axios.create({
        baseURL: api.defaults.baseURL,
        headers: {
          'Content-Type': 'application/json'
        }
      });

      // Send data to backend API without using the main api instance
      // This prevents the auth interceptor from redirecting
      const response = await axiosInstance.post('/vehicles', vehicleData);
      
      console.log('Response from backend:', response.data);
      
      // Also store in localStorage as a backup
      const vehicles = JSON.parse(localStorage.getItem('volunteerVehicles') || '[]');
      const newVehicle = {
        ...response.data.vehicle || response.data,
        id: (response.data.vehicle && response.data.vehicle._id) || response.data._id || Date.now()
      };
      vehicles.push(newVehicle);
      localStorage.setItem('volunteerVehicles', JSON.stringify(vehicles));

      toast.success('Vehicle registered successfully!');
      
      // Set success message and store registered vehicle details
      setSuccessMessage('Your vehicle has been registered successfully!');
      setRegisteredVehicle(response.data.vehicle || response.data);
      
      // Reset form
      setFormData({
        ownerName: user?.name || '',
        vehicleType: '',
        licensePlate: '',
        pincode: '',
        state: '',
        city: '',
        capacity: '',
        contactNumber: '',
        availabilityDate: '',
        availableDays: '',
        isCurrentlyAvailable: false,
        additionalNotes: '',
        vehicleImage: ''
      });
      setImagePreview(null);
      setErrorMessage('');
      
      // Scroll to top to show success message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Error registering vehicle:', error);
      console.error('Error details:', error.response?.data);
      
      const errorMsg = error.response?.data?.message || error.message || 'Failed to register vehicle. Please try again.';
      setErrorMessage(errorMsg);
      setSuccessMessage('');
      setRegisteredVehicle(null);
      
      toast.error(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 via-gray-100 to-white pt-20 pb-12">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-4xl mx-auto"
        >
          {/* Header Section */}
          <div className="text-center mb-8">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="inline-block p-4 bg-red-50 rounded-full mb-6 shadow-lg"
            >
              <Car size={40} className="text-red-600" />
            </motion.div>
            <h1 className="text-4xl font-bold mb-3 text-gray-800 bg-clip-text text-transparent bg-gradient-to-r from-red-600 to-red-500">
              Volunteer Vehicle Registration
            </h1>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Join our network of emergency transportation volunteers
            </p>
          </div>

          {/* Form Container */}
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
            <div className="p-8">
              {successMessage && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start">
                  <CheckCircle className="text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="font-medium text-green-800">Success!</h3>
                    <p className="text-green-700 mt-1">{successMessage}</p>
                    {registeredVehicle && (
                      <div className="mt-2 p-3 bg-white rounded border border-green-100">
                        <p className="text-gray-700"><strong>Vehicle Type:</strong> {registeredVehicle.vehicleType}</p>
                        <p className="text-gray-700"><strong>License Plate:</strong> {registeredVehicle.licensePlate}</p>
                        <p className="text-gray-700"><strong>Status:</strong> {registeredVehicle.status}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {errorMessage && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
                  <AlertCircle className="text-red-500 mr-3 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="font-medium text-red-800">There was an error with your submission</h3>
                    <p className="text-red-700 mt-1">{errorMessage}</p>
                  </div>
                </div>
              )}
              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Personal Information Section */}
                <div>
                  <h2 className="text-xl font-semibold mb-6 text-gray-800 flex items-center border-b pb-2">
                    <User className="mr-2 text-red-600" /> Personal Information
                  </h2>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="group">
                      <label className="block text-gray-700 font-medium mb-2 flex items-center">
                        <User size={16} className="mr-1 text-red-600" />
                        Owner Name <span className="text-red-500 ml-1">*</span>
                      </label>
                      <input
                        type="text"
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                        value={formData.ownerName}
                        onChange={(e) => setFormData({...formData, ownerName: e.target.value})}
                        placeholder="Enter your full name"
                        required
                      />
                    </div>

                    <div className="group">
                      <label className="block text-gray-700 font-medium mb-2 flex items-center">
                        <Phone size={16} className="mr-1 text-red-600" />
                        Contact Number <span className="text-red-500 ml-1">*</span>
                      </label>
                      <input
                        type="tel"
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                        value={formData.contactNumber}
                        onChange={(e) => setFormData({...formData, contactNumber: e.target.value})}
                        placeholder="Enter your contact number"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Vehicle Details Section */}
                <div>
                  <h2 className="text-xl font-semibold mb-6 text-gray-800 flex items-center border-b pb-2">
                    <Car className="mr-2 text-red-600" /> Vehicle Details
                  </h2>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="group">
                      <label className="block text-gray-700 font-medium mb-2 flex items-center">
                        <Car size={16} className="mr-1 text-red-600" />
                        Vehicle Type <span className="text-red-500 ml-1">*</span>
                      </label>
                      <select
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200 bg-white"
                        value={formData.vehicleType}
                        onChange={(e) => setFormData({...formData, vehicleType: e.target.value})}
                        required
                      >
                        <option value="">Select Vehicle Type</option>
                        {vehicleTypes.map(type => (
                          <option key={type.value} value={type.value}>{type.label}</option>
                        ))}
                      </select>
                    </div>

                    <div className="group">
                      <label className="block text-gray-700 font-medium mb-2 flex items-center">
                        <Hash size={16} className="mr-1 text-red-600" />
                        License Plate <span className="text-red-500 ml-1">*</span>
                      </label>
                      <input
                        type="text"
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                        value={formData.licensePlate}
                        onChange={(e) => setFormData({...formData, licensePlate: e.target.value})}
                        placeholder="Enter license plate number"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-3 gap-6 mt-6">
                    <div className="group">
                      <label className="block text-gray-700 font-medium mb-2 flex items-center">
                        <MapPin size={16} className="mr-1 text-red-600" />
                        State <span className="text-red-500 ml-1">*</span>
                      </label>
                      <input
                        type="text"
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                        value={formData.state}
                        onChange={(e) => setFormData({...formData, state: e.target.value})}
                        placeholder="Enter state"
                        required
                      />
                    </div>
                    
                    <div className="group">
                      <label className="block text-gray-700 font-medium mb-2 flex items-center">
                        <MapPin size={16} className="mr-1 text-red-600" />
                        City <span className="text-red-500 ml-1">*</span>
                      </label>
                      <input
                        type="text"
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                        value={formData.city}
                        onChange={(e) => setFormData({...formData, city: e.target.value})}
                        placeholder="Enter city"
                        required
                      />
                    </div>
                    
                    <div className="group">
                      <label className="block text-gray-700 font-medium mb-2 flex items-center">
                        <MapPin size={16} className="mr-1 text-red-600" />
                        Pincode <span className="text-red-500 ml-1">*</span>
                      </label>
                      <input
                        type="text"
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                        value={formData.pincode}
                        onChange={(e) => setFormData({...formData, pincode: e.target.value})}
                        placeholder="Enter your area pincode"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6 mt-6">
                    <div className="group">
                      <label className="block text-gray-700 font-medium mb-2 flex items-center">
                        <Users size={16} className="mr-1 text-red-600" />
                        Passenger Capacity <span className="text-red-500 ml-1">*</span>
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="6"
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                        value={formData.capacity}
                        onChange={(e) => setFormData({...formData, capacity: e.target.value})}
                        placeholder="Number of passengers (max 6)"
                        required
                      />
                      <small className="text-gray-500 mt-1 block">Maximum capacity: 6 passengers</small>
                    </div>
                  </div>
                </div>

                {/* Availability Section */}
                <div>
                  <h2 className="text-xl font-semibold mb-6 text-gray-800 flex items-center border-b pb-2">
                    <Calendar className="mr-2 text-red-600" /> Availability & Additional Information
                  </h2>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="group">
                      <label className="block text-gray-700 font-medium mb-2 flex items-center">
                        <Calendar size={16} className="mr-1 text-red-600" />
                        Available From <span className="text-red-500 ml-1">*</span>
                      </label>
                      <input
                        type="date"
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                        value={formData.availabilityDate}
                        onChange={(e) => setFormData({...formData, availabilityDate: e.target.value})}
                        min={new Date().toISOString().split('T')[0]}
                        required
                      />
                    </div>

                    <div className="group">
                      <label className="block text-gray-700 font-medium mb-2 flex items-center">
                        <Clock size={16} className="mr-1 text-red-600" />
                        Availability <span className="text-red-500 ml-1">*</span>
                      </label>
                      <select
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200 bg-white"
                        value={formData.availableDays}
                        onChange={(e) => {
                          // If 24/7 is selected, set to 365 days
                          const value = e.target.value === '24/7' ? '365' : e.target.value;
                          setFormData({...formData, availableDays: value});
                        }}
                        required
                      >
                        <option value="">Select Availability</option>
                        <option value="24/7">24/7 (Always Available)</option>
                        <option value="7">7 Days</option>
                        <option value="14">14 Days</option>
                        <option value="30">30 Days</option>
                        <option value="90">3 Months</option>
                        <option value="180">6 Months</option>
                        <option value="365">1 Year</option>
                        <option value="custom">Custom</option>
                      </select>
                      {formData.availableDays === 'custom' && (
                        <input
                          type="number"
                          min="1"
                          className="w-full mt-2 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                          onChange={(e) => setFormData({...formData, availableDays: e.target.value})}
                          placeholder="Enter number of days"
                          required
                        />
                      )}
                    </div>
                  </div>

                  <div className="flex items-center mt-6">
                    <input
                      type="checkbox"
                      id="isCurrentlyAvailable"
                      className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                      checked={formData.isCurrentlyAvailable}
                      onChange={(e) => setFormData({...formData, isCurrentlyAvailable: e.target.checked})}
                    />
                    <label htmlFor="isCurrentlyAvailable" className="ml-2 block text-gray-700">
                      Currently Available for Emergency Services
                    </label>
                  </div>

                  <div className="mt-6">
                    <label className="block text-gray-700 font-medium mb-2">
                      Additional Notes
                    </label>
                    <textarea
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                      rows="3"
                      value={formData.additionalNotes}
                      onChange={(e) => setFormData({...formData, additionalNotes: e.target.value})}
                      placeholder="Any additional information..."
                    />
                  </div>
                </div>

                {/* Vehicle Image Upload */}
                <div className="mt-8">
                  <h2 className="text-xl font-semibold mb-6 text-gray-800 flex items-center border-b pb-2">
                    <Car className="mr-2 text-red-600" /> Vehicle Image
                  </h2>
                  <div className="mb-6">
                    <label className="block text-gray-700 font-medium mb-2">
                      Upload Vehicle Image
                    </label>
                    <div className="flex items-center space-x-4">
                      <div className="flex-1">
                        <div className="relative border-2 border-dashed border-gray-300 rounded-lg p-6 flex flex-col items-center justify-center hover:border-red-500 transition-all duration-200">
                          <input
                            type="file"
                            accept="image/*"
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) {
                                // Convert to base64
                                const reader = new FileReader();
                                reader.onloadend = () => {
                                  setFormData({...formData, vehicleImage: reader.result});
                                  setImagePreview(reader.result);
                                };
                                reader.readAsDataURL(file);
                              }
                            }}
                          />
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-gray-400 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          <p className="text-sm text-gray-500">Click to upload or drag and drop</p>
                          <p className="text-xs text-gray-400 mt-1">PNG, JPG, GIF up to 10MB</p>
                        </div>
                      </div>
                      
                      {imagePreview && (
                        <div className="w-32 h-32 relative border rounded-lg overflow-hidden">
                          <img 
                            src={imagePreview} 
                            alt="Vehicle preview" 
                            className="w-full h-full object-cover"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              setFormData({...formData, vehicleImage: ''});
                              setImagePreview(null);
                            }}
                            className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1 hover:bg-red-700 transition-colors"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-2">Upload a clear image of your vehicle. This helps in identification during emergencies.</p>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="flex justify-end pt-6 border-t border-gray-100">
                  <motion.button
                    type="submit"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    disabled={isSubmitting}
                    className="px-8 py-3 bg-gradient-to-r from-red-600 to-red-500 
                      text-white font-medium rounded-lg flex items-center hover:shadow-lg 
                      transition-shadow duration-200 disabled:opacity-70"
                  >
                    {isSubmitting ? (
                      <>
                        <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Registering...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="mr-2 h-5 w-5" />
                        Register Vehicle
                      </>
                    )}
                  </motion.button>
                </div>
              </form>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  )
}